<template>
    <div class="gallery">
        <div class="gallery__list">
            <div v-for="(image, index) in lstImages" :key="image.id" class="gallery__1-1-container">
                <img 
                    :class="{'gallery__img--active': image.id === highlightedImage?.id}"
                    draggable="true"
                    :src="image.url"
                    alt=""
                    class="gallery__img gallery__fit-container"
                    @click.prevent="onChangeHighlightImage(index)"
                    @dragstart="onDragStart(index, image)"
                    @dragend="onDragEnd($event)"
                    @dragenter="onDragEnter($event)"
                    @dragover="onDragOver($event, index, image)"
                    @dragleave="onDragLeave(index)"
                />
            </div>
            <div class="gallery__1-1-container">
                <button class="gallery__fit-container gallery__add" @click.prevent="openUpload">
                    <i aria-hidden="true" class="fas fa-plus"></i>
                </button>
            </div>
        </div>
        <div v-show="showUpload" class="gallery__content">
            <vue-dropzone
                :key="dropzoneKey"
                id="dropzone"
                ref="myVueDropzone"
                :options="dropzoneOptions"
                @vdropzone-success-multiple="uploudSuccess"/>
            <slot></slot>
        </div>
        <div v-show="!showUpload" class="gallery__content">
            <div class="gallery__1-1-container">
                <img v-if="highlightedImage" :src="highlightedImage.url" alt=""
                     class="gallery__img gallery__fit-container"/>
                <p v-else class="gallery__placeholder-text">Ingen billeder</p>

                <button 
                    v-if="hasImages && highlightedImage"
                    class="gallery__remove" 
                    @click.prevent="onDelete"
                >
                    <i aria-hidden="true" class="fas fa-times"></i>
                </button>
            </div>
            <slot></slot>
        </div>
    </div>
</template>

<script>
import vue2Dropzone from 'vue2-dropzone'
import 'vue2-dropzone/dist/vue2Dropzone.min.css'
import Cookies from 'js-cookie'

export default {
    props: {
        item: {
            type: Object,
            required: true,
        },
        images: {
            type: Array,
            required: true,
            validator: (value) => {
                return value.every(img => img.id !== undefined && img.url !== undefined);
            }
        }
    },
    components: {
        vueDropzone: vue2Dropzone
    },
    data() {
        return {
            dropzoneKey: 0,
            showUpload: false,
            highlightedImageIndex: 0,
            dropzoneOptions: {
                url: '/api/images/' + this.item.id,
                thumbnailWidth: 150,
                maxFilesize: 10.0,
                headers: { "Authorization": "Bearer " + Cookies.get('api_token') },
                uploadMultiple: true,
            },
            draggingImage: null,
            startIndex: 0,
            dragOver: -1,
            lstImages: [],
            isDragging: false,
            highlightedImageId: 0,
        }
    },
    watch: {
        images: {
            handler: function (val, oldVal) {
                const newImages = val.filter(img => !oldVal.some(oldImg => oldImg.id === img.id));

                if (newImages.length > 0) {
                    this.highlightedImageIndex = val.findIndex(img => img.id === newImages[0].id);
                }
                
                this.showUpload = false;
                this.lstImages = val;
            },
        },
    },
    computed: {
        highlightedImage() {
            if (this.isDragging && this.highlightedImageId) {
                return this.lstImages.find(img => img.id === this.highlightedImageId);
            }

            if (this.showUpload) {
                return null;
            }

            if (this.lstImages.length > 0) {
                return this.lstImages[this.highlightedImageIndex];
            }

            if (this.item.manufacturer?.logo) {
                return {
                    url: this.item.manufacturer.logo,
                }
            }

            return null;
        },
        hasImages() {
            return this.lstImages.length > 0;
        }
    },
    mounted() {
        this.lstImages = this.images;
    },
    methods: {
        updateSortOrder() {
            window.axios.post('/api/items/' + this.item.id + '/sort-images', {
                sort_order: this.lstImages.map((img, index) => {
                    return {
                        id: img.id,
                        sort_order: index + 1,
                    }
                })
            }).then(() => {
                this.$store.dispatch('setSuccess', 'Billederne blev sorteret')
            })
            .catch((error) => {
                console.error(error);
                this.$store.dispatch('setError', 'Billederne kunne ikke sorteres')
            })
        },
        onDragStart(index, image) {
            this.highlightedImageId = this.highlightedImage.id;
            this.startIndex = index;
            this.draggingImage = image;
            this.isDragging = true;
        },
        onDragEnd(e) {
            if (this.dragOver >= 0 && this.dragOver !== this.startIndex) {
                this.highlightedImageIndex = this.lstImages.findIndex(img => img.id === this.highlightedImageId);
                this.updateSortOrder();
                this.dragOver = -1;
            }
            this.isDragging = false;
        },
        onDragEnter(e) {
            e.preventDefault();
        },
        onDragOver(e, index, image) {
            e.preventDefault();

            if (!this.isDragging) {
                return;
            }

            if (image.id === this.draggingImage.id) {
                return;
            }

            if (this.dragOver >= 0) {
                // Revert previous move if no leave event was fired
                const element = this.lstImages[this.dragOver];
                this.lstImages.splice(this.dragOver, 1);
                this.lstImages.splice(this.startIndex, 0, element);
            }
            this.dragOver = index;
            const element = this.lstImages[this.startIndex];
            this.lstImages.splice(this.startIndex, 1);
            this.lstImages.splice(index, 0, element);
        },
        onDragLeave(index) {
            if (index !== this.dragOver) {
                return;
            }

            const element = this.lstImages[index];
            this.lstImages.splice(index, 1);
            this.lstImages.splice(this.startIndex, 0, element);
            this.dragOver = -1;
        },
        uploudSuccess(file, response) {
            this.$emit('imagesUploaded', response);
        },
        openUpload() {
            this.dropzoneKey += 1;
            this.showUpload = true;
        },
        onChangeHighlightImage(index) {
            this.highlightedImageIndex = index;
            this.showUpload = false;
        },
        onDelete() {
            if (window.confirm('Er du sikker på, at du vil slette dette billede?')) {
                this.$emit('delete', this.highlightedImage);
                this.lstImages = this.lstImages.filter(img => img.id !== this.highlightedImage.id);
                if (this.highlightedImageIndex >= this.lstImages.length) {
                    this.highlightedImageIndex = this.lstImages.length - 1;
                }
            }
        },
    },
}
</script>

<style lang="scss" scoped>
@import '~#/globals';

.gallery {
    display: flex;

    &__list {
        flex-shrink: 1;
        margin-right: 1rem;
        width: 60px;

        > *:not(:last-child) {
            margin-bottom: 0.5rem;
        }
    }

    &__content {
        width: 100%;
    }

    &__1-1-container {
        background: #f9f9f9;
        flex-grow: 1;
        height: 0;
        padding-bottom: 100%;
        position: relative;
        width: 100%;
    }

    &__fit-container {
        height: 100%;
        left: 0;
        position: absolute;
        top: 0;
        width: 100%;
    }

    &__close-upload {
        //background: transparent;
        border: none;
        cursor: pointer;
        height: 35px;
        outline: none;
        width: 100%;
    }

    &__remove {
        background: transparent;
        border: none;
        cursor: pointer;
        height: 20px;
        outline: none;
        position: absolute;
        right: 5px;
        top: 5px;
        width: 20px;

        .fas {
            color: $red;
            font-size: 1em;
        }
    }

    &__add {
        border: none;
        cursor: pointer;
        outline: none;

        .fas {
            color: $green;
            font-size: 1.5em;
            transition: transform 300ms;
        }

        &:hover .fas {
            transform: scale(1.3);
        }
    }

    &__img {
        object-fit: scale-down;

        &--active {
            border: 1px solid #333;
        }

        .gallery__list &:hover {
            cursor: grab;
        }
    }

    &__placeholder-text {
        align-items: center;
        display: flex;
        height: 100%;
        justify-content: center;
        position: absolute;
        width: 100%;
    }
}
</style>
